import * as React from 'react';
import { ACTION_NAVIGATE_TO_JOIN } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button, Divider, TockLogo, Typography } from 'omakase';
import { IconExternalLink } from '@tock/icons';
import { Theme, css } from '@emotion/react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  modifyCookiePrefsImgSrc,
  modifyCookiePrefsLabelText,
  onOpenCookieConsentPrefs,
} from '@tock/shared/utils/cookieConsentUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useLocation } from 'react-router';
import Anchor from 'components/Anchor';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'none',
      alignItems: 'center',
      backgroundColor: theme.colors.bgDefault,
      borderTop: `1px solid ${theme.colors.borderDefault}`,
      justifyContent: 'center',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
    },
    rootTopPadding: {
      [theme.breakpoints.up('sm')]: {
        paddingTop: theme.spacing(12 + 8.5),
      },
    },
    mainLinksLink: {
      color: 'inherit',
      textDecoration: 'none',
      '@media(hover: hover) and (pointer: fine)': {
        '&:hover': {
          color: theme.colors.fgContrast,
        },
      },
    },
    cookieConsentLink: {
      cursor: 'pointer',
    },
    mainFooter: {
      display: 'grid',
      background: '#000000',
      padding: theme.spacing(3.5),
      gap: theme.spacing(3),
    },
    mainFooterSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fill: 'white',
    },
    mainFooterSeparatedContent: {
      display: 'flex',
      gap: '28px',
    },
    mainFooterLink: {
      color: theme.colors.fgContrast,
      textDecoration: 'none',
      '@media(hover: hover) and (pointer: fine)': {
        '&:hover': {
          color: theme.colors.fgContrast,
        },
      },
    },
    mainFooterDivider: {
      background: theme.colors.bgDark + ' !important',
    },
  })
);

interface Props {
  isMobileNative: boolean;
  hasTopPadding?: boolean;
}
const Footer: React.FunctionComponent<Props> = ({ isMobileNative, hasTopPadding }) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const classes = useStyles();
  const wwwUrl = `https://www.exploretock.com`;

  const currentYear = new Date().getFullYear();

  const cookieConsentButtonText = modifyCookiePrefsLabelText();
  const cookieConsentImgSrc = modifyCookiePrefsImgSrc();
  const { search } = useLocation();

  const baseUrl = 'https://resy.com/join/get-started/';
  const formattedSearch = search.startsWith('?') ? `&${search.slice(1)}` : search;
  const href = `${baseUrl}${formattedSearch}`;

  return (
    <>
      <footer
        className={clsx(classes.root, {
          [classes.rootTopPadding]: hasTopPadding,
        })}
      >
        <div className={classes.mainFooter}>
          <div className={classes.mainFooterSection}>
            <TockLogo
              css={({ spacing, colors }: Theme) => css`
                width: ${spacing(11)}px;
                color: ${colors.fgContrast};
              `}
            />
            <ul className={classes.mainFooterSeparatedContent}>
              <li>
                <Anchor className={classes.mainFooterLink} href="/help">
                  <Typography component="span" color="inherit" variant="body02">
                    Support
                  </Typography>
                </Anchor>
              </li>
              <li>
                <Anchor className={classes.mainFooterLink} href={`${wwwUrl}/terms`} target="_blank">
                  <Typography component="span" color="inherit" variant="body02">
                    Terms
                  </Typography>
                </Anchor>
              </li>
              <li>
                <Anchor
                  className={classes.mainFooterLink}
                  href={`${wwwUrl}/privacy`}
                  target="_blank"
                >
                  <Typography component="span" color="inherit" variant="body02">
                    Privacy
                  </Typography>
                </Anchor>
              </li>
              <li>
                <Anchor
                  className={classes.mainFooterLink}
                  href={`${wwwUrl}/privacy-specific`}
                  target="_blank"
                >
                  <Typography component="span" color="inherit" variant="body02">
                    Specific Privacy Notices
                  </Typography>
                </Anchor>
              </li>
              <li>
                <Anchor
                  className={classes.mainFooterLink}
                  href="https://www.exploretock.com/join/careers/"
                  target="_blank"
                >
                  <Typography component="span" color="inherit" variant="body02">
                    Careers
                  </Typography>
                </Anchor>
              </li>
              {cookieConsentButtonText ? (
                <li className={classes.mainFooterLink} data-testid="cookie-preference-button">
                  {cookieConsentImgSrc && (
                    <span
                      css={({ spacing }: Theme) => css`
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        margin-right: ${spacing(1)}px;
                      `}
                    >
                      <img
                        alt="US State Privacy Opt-Out Icon"
                        src={cookieConsentImgSrc}
                        width="24px"
                      />
                    </span>
                  )}
                  <button
                    onClick={onOpenCookieConsentPrefs}
                    className={clsx(classes.mainLinksLink, classes.cookieConsentLink)}
                  >
                    <Typography component="span" color="inherit" variant="body02">
                      {cookieConsentButtonText}
                    </Typography>
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
          <Divider className={classes.mainFooterDivider} />
          <div className={classes.mainFooterSection}>
            <div className={classes.mainFooterSeparatedContent}>
              <Typography
                component="span"
                variant="body02"
                color="fgContrast"
                className="MainFooter-tock"
              >
                &copy; {currentYear} Tock, LLC
              </Typography>
              <Typography
                component="span"
                variant="body02"
                color="fgContrast"
                className="MainFooter-tock"
              >
                Explore Tock &reg;
              </Typography>
            </div>
            <Button
              component={Anchor}
              href={href}
              size="small"
              variant="highContrast"
              iconEnd={<IconExternalLink />}
              target="_blank"
              onClick={() =>
                amplitudeSendEvent(ACTION_NAVIGATE_TO_JOIN, {
                  pageRegion: 'Footer',
                })
              }
            >
              Use Tock at your business
            </Button>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
